import {
  buildExampleSearchTerm,
  EXAMPLE_VEHICLE,
  IInitialPriceEstimateRequest,
} from "../../models/Category/InitialPriceEstimate";
import { withSearchValues } from "../../models/SerpQuery/SerpQuery";
import SerpQueryPreview from "./SerpQueryPreview";

interface SearchTermPreviewProps {
  config: IInitialPriceEstimateRequest;
  categoryName: string;
  categoryEbayCategoryId: string | null;
  /** The saved query from the API; the per-search parts are swapped for the live form values. */
  exampleSerpQuery?: string | null;
}

/** What a lead for the example vehicle would search, built live from the unsaved form. */
export default function SearchTermPreview({
  config,
  categoryName,
  categoryEbayCategoryId,
  exampleSerpQuery,
}: SearchTermPreviewProps) {
  // Blank means no keyword, exactly as Core searches it.
  const keyword = config.searchKeyword?.trim() || null;
  const term = buildExampleSearchTerm(config.searchFields, keyword);
  const ebayOverride = config.ebayCategoryId?.trim();
  const ebayCategoryId = ebayOverride || categoryEbayCategoryId;
  const liveQuery = exampleSerpQuery
    ? withSearchValues(exampleSerpQuery, { _nkw: term, category_id: ebayCategoryId })
    : null;

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">
        Example Search
      </h2>

      <p className="text-xs text-gray-500">
        For a {EXAMPLE_VEHICLE.Year} {EXAMPLE_VEHICLE.Make}{" "}
        {EXAMPLE_VEHICLE.Model} {EXAMPLE_VEHICLE.Series}{" "}
        {EXAMPLE_VEHICLE.EngineCapacity} {EXAMPLE_VEHICLE.FuelType}, eBay would
        be searched for:
      </p>

      <p className="mt-2 rounded bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 break-words">
        {term || "—"}
      </p>

      <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-gray-500">eBay category</dt>
        <dd className="text-gray-800">
          {ebayCategoryId ?? "All categories"}
          {!ebayOverride && ebayCategoryId && (
            <span className="ml-1 text-gray-500">(from the category)</span>
          )}
        </dd>
        <dt className="text-gray-500">Listings</dt>
        <dd className="text-gray-800">
          Live, used, from the sellers in Serp settings
        </dd>
      </dl>

      {!keyword && !ebayCategoryId && (
        <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          With no keyword and no eBay category, this searches for every part
          on the vehicle, not {categoryName}. Add one or the other.
        </p>
      )}

      <p className="mt-3 text-xs text-gray-500">
        The lead shows the lowest, average and highest asking price of the
        matches. Sold prices are not used.
      </p>

      {liveQuery && (
        <div className="mt-4">
          <SerpQueryPreview
            title="Serp query"
            query={liveQuery}
            hint="The full query SerpApi receives for this example, from the saved Serp settings. Open in SerpApi runs it in the playground with your own key."
          />
        </div>
      )}
    </section>
  );
}
